import type { BadgeMedallionRow } from "@/types/badges"
import type { BrandMissionCollabRow } from "@/types/missions"
/**
 * OKS-25 / OKS-26 — Inertia prop shapes for the Crew surfaces.
 *
 * - OKS-25: roster + quick-stats — `pages/Brand/Crew/Index.tsx`, `crewPath()`.
 * - OKS-26: creator detail (brand view) — `pages/Brand/Crew/Show.tsx`,
 *   `crewMembershipPath(id)`. Nested `membership: { creator: {...} }`
 *   prop tree per BE TRD rev-5; wire namespace `membership[*]`; URL-keyed
 *   `actions` (hide-when-null discriminator); `status_history` with
 *   `actor: { id, name } | null` + `at` ISO8601 (null actor renders
 *   localized "System"/"Système" via `crew.detail.status_history.actor_system`).
 *
 * **Locked at TRD v5 publish + verified against**:
 *   - Backend TRD v2 §API Surface (canonical contract)
 *   - `app/controllers/crew_memberships_controller.rb` — `serialise_row`,
 *     `stats_props`, `filter_options_props`
 *   - `app/services/crew_memberships/{list_query,quick_stats,filter_options_digest}.rb`
 *
 * Every snake_case field mirrors the Rails Inertia serialiser 1:1; the
 * FE never re-shapes on the wire.
 *
 * **Wire-format conventions (BE-attested):**
 *   - `members[].engagement_rate` ships as a JSON `number | null`. BE
 *     coerces with `to_f` (NOT `to_d`) so the round-trip is exact for
 *     every legal `decimal(5,2)` value over 0..100.
 *   - `members[].avatar_url` is always `null` in V1 (forward-compat
 *     slot); FE renders `<AvatarFallback>` initials from `display_name`.
 *   - `location.country` is ISO-3166-1 alpha-2 (e.g. `"FR"`); FE
 *     expands to localized country names via `Intl.DisplayNames` at
 *     render only — the wire stays alpha-2.
 *   - `tags` are plain strings (semicolon-text source on
 *     `crew_memberships.tags`, mirrored to a string array via
 *     `tag_list` on the model).
 *   - `stats.content_produced` is `null` in V1; OKS-32 wires.
 *   - `filter_options.followers_count` and `filter_options.joined_at`
 *     are `null` when the brand has zero active memberships
 *     (FilterOptionsDigest hint surfaces).
 */

// ─── Sort + filter shape ───────────────────────────────────────────

/** Whitelisted sort fields per BE TRD v2 §Decisions and
 *  `CrewMemberships::ListQuery::SORTABLE_FIELDS`. */
export type CrewSortField = "joined_at" | "follower_count"

export type CrewSortDir = "asc" | "desc"

export interface CrewSort {
  field: CrewSortField
  dir: CrewSortDir
}

/**
 * Echoed normalized filter state. BE re-emits whatever the URL params
 * resolved to after PORO normalization (drops empties, canonicalizes
 * date formats, strips off-whitelist entries).
 *
 * `location` is a single composite string (e.g. `"Paris, FR"`) — BE
 * exact-matches against `users.city || ', ' || users.country`. The FE
 * Location filter is a single shadcn `Select` populated from
 * `filter_options.locations`; option value IS the wire string.
 */
export interface CrewFilters {
  q: string | null
  /**
   * OKS-348 — Composite location filter `"City, ISO-2"` (e.g.
   * `"Paris, FR"`). Single source of truth — the pre-OKS-348
   * `canonical_location_id` branch is gone; pre-existing segment
   * rules were one-shot rewritten to this shape.
   */
  location: string | null
  followers_min: number | null
  followers_max: number | null
  tags: string[]
  joined_from: string | null // ISO date "YYYY-MM-DD"
  joined_to: string | null
  /**
   * OKS-27 — At-risk lifecycle filter. URL wire format `?at_risk=1`
   * (NOT `=true`); BE casts via `ActiveModel::Type::Boolean` and echoes
   * the boolean back on `filters.at_risk`. Default `false`.
   *
   * Server-scoped union: a row matches when EITHER `lifecycle_alerts.rotation`
   * or `lifecycle_alerts.inactivity` is non-null. Composes AND with all
   * other filters per BE TRD §API Surface.
   */
  at_risk: boolean
  /**
   * OKS-58 — Masterclass-completion rule. Both keys travel as a PAIR
   * (BE: `CrewSegments::Rules` write-time + `CrewMemberships::ListQuery`
   * eval-time both enforce the both-or-neither invariant). FE mirrors:
   * the URL emits both or neither (`useCrewListUrl.buildCrewListParams`);
   * the active-filter chip + the filter-rail field component package
   * them together. Inactive rule = both `null`.
   */
  masterclass_completion_op: "is_completed" | "is_not_completed" | null
  masterclass_completion_id: number | null
}

/**
 * OKS-58 — Masterclass-completion operator. Closed set; URL emits the
 * raw string literal. Off-whitelist values silently drop the rule at
 * the BE `apply_masterclass_completion` step (forgiving wire).
 */
export type MasterclassCompletionOp = "is_completed" | "is_not_completed"

/**
 * OKS-58 — Single picker item shipped on `filter_options.published_masterclasses`.
 * `topic` powers the picker's 2-line `CommandItem` (title + muted topic).
 * Wire-locked bilateral with LQE (TRD KD-3 + §3 FilterOptionsDigest).
 */
export interface PublishedMasterclassPickerItem {
  id: number
  title: string
  topic: string
}

// ─── OKS-27 — Lifecycle alert wire shape ───────────────────────────

/**
 * Lifecycle alerts surfaced on every active membership (roster row +
 * detail header). Server-computed against per-brand thresholds that
 * are NOT shipped to the FE — the FE consumes only object-presence
 * (badge fires on non-null) plus the BE-computed integer counts and
 * `kind` discriminator for the inactivity tooltip variant.
 *
 * Object-presence semantics (BE rev-2 nested canonical, frozen):
 *   - `rotation: { months: N }`     → render Rotation pill with N months
 *   - `rotation: null`              → no Rotation pill
 *   - `inactivity: { kind, weeks: N }` → render Inactivity pill (kind drives tooltip variant)
 *   - `inactivity: null`            → no Inactivity pill
 *
 * `kind` is the discriminator for the inactivity tooltip:
 *   - `"post_mission"`     → BE `last_completed_mission_at IS NOT NULL`
 *   - `"joined_fallback"`  → BE `last_completed_mission_at IS NULL`
 *                            (counts from `joined_at`)
 *
 * Counts are floor-truncated integers in brand TZ — the FE NEVER
 * recomputes these, NEVER reads thresholds, NEVER reads brand settings.
 */
export interface LifecycleAlerts {
  rotation: { months: number } | null
  inactivity: { kind: "post_mission" | "joined_fallback"; weeks: number } | null
}

// ─── Member row ────────────────────────────────────────────────────

/**
 * Single crew row. Flat shape (no nested `user: { … }`) per BE
 * controller §`serialise_row`.
 */
export interface CrewMember {
  /** `crew_memberships.id` (Rails integer, JSON number). */
  id: number
  /**
   * Per-brand `Candidate#id` for the OKS-23 detail-page link target.
   * `null` when no per-brand candidate row exists for the user (seed
   * edge / cross-brand ghosts — see `candidate_ids_for` controller
   * helper).
   */
  candidate_id: number | null
  /** Always `null` in V1; forward-compat slot for the avatar pipeline. */
  avatar_url: string | null
  /** `User#name` — used as the row's display name + initials source. */
  display_name: string
  /** `User#tiktok_handle` — rendered as `@{handle}`. */
  tiktok_handle: string
  /**
   * `User#city` + `User#country` (alpha-2). FE renders the localized
   * country name via `lib/countries.ts#countryLabel(code, locale)`.
   * Post-OKS-348 both fields are NOT NULL at the DB layer; the wire
   * types stay `string | null` for forward-compat with future paths
   * that may opt out.
   */
  location: {
    city: string | null
    country: string | null
  }
  /** Snapshot from `crew_memberships.followers_count` at join. */
  follower_count: number | null
  /**
   * `crew_memberships.engagement_rate_percent` exposed as
   * `engagement_rate` on the wire. Percent (0..100, two decimals);
   * `null` when not set yet (manual entry on OKS-23 detail surface).
   */
  engagement_rate: number | null
  /** `crew_memberships.tags` (semicolon-text → string[] via `tag_list`). */
  tags: string[]
  /** ISO-8601 datetime of join (`crew_memberships.joined_at#iso8601`). */
  joined_at: string
  /**
   * OKS-27 — Lifecycle alerts (object-presence drives badge render).
   * Always present on active rows (BE never omits the key); each
   * sub-key is `null` when the alert does not fire.
   */
  lifecycle_alerts: LifecycleAlerts
  /**
   * OKS-27 — ISO8601 timestamp of the membership's last completed
   * mission. NULL on launch (missions ship cycle 3) — exposed sibling
   * to `joined_at` for future "last activity" affordances. NOT used
   * for tooltip count math (BE `inactivity.weeks` is authoritative).
   */
  last_completed_mission_at: string | null
  /**
   * OKS-48 — Per-brand XP balance (UX Spec §2.3 — chip on roster row).
   * SUM of `XpEvent.points` for this membership. JSON integer ≥ 0;
   * `0` on a membership with no awarded XP yet (never `null`, never
   * `—` — UX Spec §3 empty-state matrix renders "XP: 0").
   */
  xp_balance: number
  /**
   * OKS-49 — Per-row rank (UX Spec §2.2 — chip on roster row, sibling
   * of the XP chip). Resolved by the controller from
   * `RankQuery.batched_current_for` (per-brand ladder + per-row XP
   * balance). `null` is theoretical only — every brand has a default
   * ladder seeded by `Brand.after_create_commit` + the backfill data
   * migration. FE renders the chip iff `rank != null`.
   */
  rank: { name: string } | null
}

// ─── Stats / pagination / filter options ───────────────────────────

/**
 * Quick-stats strip values (PRD §5 — describes the **unfiltered**
 * active crew). `content_produced` is `null` in V1 — OKS-32 wires it
 * to month-to-date approved-content counts.
 */
export interface CrewStats {
  crew_size: number
  avg_engagement: number | null
  content_produced: number | null
}

/**
 * Pagination 5-key shape (matches OKS-17 idiom + BE
 * `CrewMembershipsController#pagination_props`). `prev`/`next`
 * are full URL paths (or `null` at the boundary).
 */
export interface CrewPagination {
  page: number
  pages: number
  count: number
  prev: string | null
  next: string | null
}

/**
 * Filter-rail options digest (BE
 * `CrewMemberships::FilterOptionsDigest`). All four keys are present
 * on every page render; the two range hints can be `null` when the
 * brand has zero active memberships.
 */
export interface CrewFilterOptions {
  /**
   * OKS-348 — DISTINCT `"<City>, <ISO-2>"` composite strings across
   * the brand-scoped active CrewMembership relation, alpha-sorted.
   * FE may localize the country half via `lib/countries.ts#countryLabel`.
   */
  locations: string[]
  /** DISTINCT tag strings across active memberships. */
  tags: string[]
  /** Range hint for the Followers Popover placeholder values. */
  followers_count: { min: number; max: number } | null
  /** Range hint for the Joined Popover placeholder values. */
  joined_at: { earliest: string; latest: string } | null
  /**
   * OKS-58 — Picker payload for the Masterclass-completion rule.
   * Brand-scoped + state_published-floored. `[]` when the brand has
   * zero published masterclasses → drives the empty-state Popover
   * variant (UX Spec §3 Empty). Alpha-sorted by title.
   *
   * Wire-locked bilateral with LQE: `[{id, title, topic}]`. `topic`
   * powers the picker's 2-line `CommandItem`. `state` is intentionally
   * omitted — every row is `published` by scope construction.
   */
  published_masterclasses: PublishedMasterclassPickerItem[]
}

// ─── Page-level Inertia props ──────────────────────────────────────

/**
 * Top-level Inertia props for `pages/Brand/Crew/Index.tsx`. Matches
 * `CrewMembershipsController#index` 1:1.
 *
 * `is_empty` is a boolean derived server-side from
 * `base_relation.exists?` over the **unfiltered** active relation —
 * drives the empty-state vs no-results discrimination.
 *
 * **OKS-28 extensions** (locked w/ LQE contract spec):
 *   - `applied_segment` — `{id, name}` when `?segment=<id>` is in
 *     play, else `null`. Drives the chip + selector "current value".
 *   - `crew_segments` — alpha-sorted selector payload. Shipped on
 *     every render (selector is always visible per UX Spec §2.1).
 *     `members_count` is the LOCKED plural-with-s wire key.
 */
export interface CrewIndexProps {
  members: CrewMember[]
  stats: CrewStats
  filters: CrewFilters
  sort: CrewSort
  pagination: CrewPagination
  filter_options: CrewFilterOptions
  is_empty: boolean
  applied_segment: { id: number; name: string } | null
  crew_segments: { id: number; name: string; members_count: number }[]
}

// ─── Sort-Select helpers ───────────────────────────────────────────

/**
 * UX-Spec-locked sort options surfaced on the mobile `<Select>` and
 * mirrored as discrete column-header sort buttons on desktop. Each
 * option resolves to a `(field, dir)` pair that maps onto the URL
 * keys `?sort=…&dir=…`.
 *
 * The four options are:
 *   - `newest`         → `joined_at desc`   (BE default; written as
 *                                            absent params)
 *   - `oldest`         → `joined_at asc`
 *   - `most_followers` → `follower_count desc`
 *   - `fewest_followers` → `follower_count asc`
 */
export type CrewSortOption =
  | "newest"
  | "oldest"
  | "most_followers"
  | "fewest_followers"

export const CREW_SORT_OPTIONS: readonly {
  value: CrewSortOption
  field: CrewSortField
  dir: CrewSortDir
}[] = [
  { value: "newest", field: "joined_at", dir: "desc" },
  { value: "oldest", field: "joined_at", dir: "asc" },
  { value: "most_followers", field: "follower_count", dir: "desc" },
  { value: "fewest_followers", field: "follower_count", dir: "asc" },
]

/**
 * Map a `(field, dir)` pair back to the named option (used by the
 * mobile `<Select>` and desktop column-header `aria-sort` to derive
 * the active state from the URL-driven `sort` prop).
 *
 * Falls back to `"newest"` for any off-whitelist combo — matches the
 * BE silent-fallback behaviour in `ListQuery#resolved_sort`.
 */
export function resolveCrewSortOption(sort: CrewSort): CrewSortOption {
  const match = CREW_SORT_OPTIONS.find(
    (option) => option.field === sort.field && option.dir === sort.dir,
  )
  return match?.value ?? "newest"
}

// ─── OKS-26 — Creator detail shapes ────────────────────────────────

/**
 * 4-value brand-side membership status enum. Mirrors `CrewMembership::STATUSES`
 * minus `mismatch_quarantine` (which BE filters out at the policy
 * relation_scope — quarantined rows return 404 to the brand surface).
 *
 * Order = pipeline order:
 *   `pending_charter` → `active` → `paused` ↔ `active` (via Pause/Unpause) → `archived` (terminal)
 */
export type CrewMembershipStatus =
  | "pending_charter"
  | "active"
  | "paused"
  | "archived"

export const CREW_MEMBERSHIP_STATUSES: readonly CrewMembershipStatus[] = [
  "pending_charter",
  "active",
  "paused",
  "archived",
] as const

/**
 * Single `status_history` event row. BE-locked shape (rev-5):
 *   `{ from, to, actor: { id, name } | null, at }`
 *
 * Surfaced wire keys:
 *   - `actor` is `null` for synthetic creation rows (migration backfill)
 *     AND system-created onboarding events (OKS-22). FE renders the
 *     null-actor row as italic muted "System" / "Système" via
 *     `crew.detail.status_history.actor_system`.
 *   - `at` is ISO-8601 (DB column `occurred_at` aliased on the wire).
 *   - `from` is `null` ONLY on the synthetic creation row.
 *
 * Reason is intentionally excluded from V1 — `pause`/`unpause`/`archive`
 * don't carry a public reason on the FE timeline. (BE persists optional
 * `reason` on `archive` events but doesn't surface it on the V1 wire.)
 */
export interface CrewStatusEvent {
  from: CrewMembershipStatus | null
  to: CrewMembershipStatus
  actor: { id: number; name: string } | null
  /** ISO-8601. */
  at: string
}

/**
 * Per-membership transition + update affordance URLs. **Hide-when-null
 * is the SINGLE visibility discriminator** — null URL → button DOM-absent
 * (no disabled-with-tooltip path). BE TRD rev-5 §Action availability matrix
 * drives nullability:
 *
 * | from status      | pause | unpause | archive |
 * |------------------|-------|---------|---------|
 * | pending_charter  | ❌    | ❌      | ✅      |
 * | active           | ✅    | ❌      | ✅      |
 * | paused           | ❌    | ✅      | ✅      |
 * | archived         | ❌    | ❌      | ❌      |
 *
 * `update_url` is non-null whenever `can_edit` is true. All transition
 * URLs route to `transitionsCrewMembershipPath(id)` with payload
 * `{transition: {event: "pause"|"unpause"|"archive"}}`.
 */
export interface CrewMemberActions {
  update_url: string
  pause_url: string | null
  unpause_url: string | null
  archive_url: string | null
}

/**
 * TikTok facts snapshot (taken at join). Read-only on the brand surface;
 * no edit affordance ships in V1.
 */
export interface CreatorSnapshot {
  /** = `users.id`. */
  id: number
  tiktok_handle: string
  /** BE-supplied (FE never reconstructs from handle). */
  tiktok_url: string
  /** = `users.name`. */
  display_name: string
  avatar_url: string | null
  /** Structured + OKS-341 canonical additions (display_label, unresolved). */
  location: {
    city: string | null
    country: string | null
    display_label?: string | null
    unresolved?: boolean
  }
  bio: string | null
  follower_count: number | null
}

/**
 * Nested `membership` prop root. Matches the BE-locked wire form
 * namespace `membership[*]` for `#update` strong-params:
 *   `params.require(:membership).permit(:notes, :engagement_rate, tags: [])`
 *
 * Field asymmetries vs. DB columns (intentional, BE-documented):
 *   - DB `engagement_rate_percent` ↔ wire `engagement_rate`
 *   - DB `users.name` ↔ wire `creator.display_name` / `notes_updated_by.name`
 */
export interface CrewMembershipDetail {
  id: number
  status: CrewMembershipStatus
  /** ISO-8601, ALWAYS present (BE backfills from user join time if missing). */
  joined_at: string
  creator: CreatorSnapshot
  /** 0–100 percent, two decimals; null when unset. */
  engagement_rate: number | null
  tags: string[]
  /** 4 000 char cap (BE CHECK + model validation). */
  notes: string | null
  notes_updated_by: { id: number; name: string } | null
  notes_updated_at: string | null
  /**
   * OKS-27 — Lifecycle alerts (object-presence drives badge render).
   * Sibling to `joined_at` on the membership tree; same wire shape as
   * the roster row. BE TRD rev-2 §API Surface.
   */
  lifecycle_alerts: LifecycleAlerts
  /** OKS-27 — Sibling timestamp; same semantics as `CrewMember.last_completed_mission_at`. */
  last_completed_mission_at: string | null
  /**
   * OKS-48 — Per-brand XP balance (UX Spec §2.4 — read-only stat tile
   * on the brand-side creator detail page). SUM of `XpEvent.points`
   * for this membership. Same shape + semantics as `CrewMember.xp_balance`.
   */
  xp_balance: number
  /**
   * OKS-49 — Per-brand rank (UX Spec §2.3 — read-only stat tile sibling
   * to `xp_balance`). Same minimal wire shape as the roster row. FE
   * renders the tile iff `rank != null`.
   */
  rank: { name: string } | null
}

/**
 * Top-level Inertia props for `pages/Brand/Crew/Show.tsx`. Locked
 * bilateral with @backend (rev-5) + @ux (V2).
 *
 * `back_url` is BE-supplied (preserves the roster filter URL when
 * reachable). FE never reconstructs from referrer.
 *
 * `collab_history` (OKS-1038) is a DEFERRED prop: `undefined` until the
 * partial reload after first paint resolves. See {@link CrewMemberCollabHistory}.
 */
export interface CreatorShowProps {
  membership: CrewMembershipDetail
  status_history: CrewStatusEvent[]
  brand_tags: string[]
  actions: CrewMemberActions
  collab_history?: CrewMemberCollabHistory
  /** OKS-57 — Per-creator masterclasses-completed analytics payload. */
  completed_masterclasses: CompletedMasterclassesPayload
  can_edit: boolean
  /**
   * OKS-396 — RECENT KUDOS strip payload: the current brand's most-recent
   * kudos for this creator, newest first, max 3 (BE-capped). Empty array
   * → FE renders the empty state. Brand-scoped server-side (P0).
   */
  recent_kudos: RecentKudo[]
  /**
   * OKS-396 — Send-permission discriminator for the inline kudo composer
   * (= `KudoPolicy#create?`). NOT derived from `actions.update_url`
   * (which is null on archived members); kudos are not status-gated.
   * Composer is hidden when false; RECENT KUDOS still renders.
   */
  can_send_kudo: boolean
  /**
   * OKS-969 — earned-badge medallions, ONE row per badge family, already
   * ordered server-side (tier desc → newest-award-in-family desc → family
   * identifier asc). `[]` for a creator with no awards — never null, never an
   * absent key. The frontend renders the order it is handed and never re-sorts.
   */
  badges: BadgeMedallionRow[]
  back_url: string
}

/**
 * OKS-396 — One row in the RECENT KUDOS strip. `sender.name` is
 * BE-resolved with an email fallback (never blank). `created_at` is
 * ISO-8601; the KudoCard formats it relative/short client-side.
 */
export interface RecentKudo {
  id: number
  message: string
  created_at: string
  sender: { id: number; name: string }
}

/**
 * OKS-57 — Single row in the per-creator Masterclasses-completed list.
 *
 * `state` lets the FE render an "Unpublished" badge on a row whose
 * masterclass was unpublished after this creator completed it (PRD §3 +
 * UX Spec §2). Row stays linked regardless (TRD KD-8).
 */
export interface CompletedMasterclassItem {
  id: number
  title: string
  topic: string
  state: "draft" | "published"
  /** ISO-8601, ALWAYS present. */
  completed_at: string
}

/**
 * OKS-57 — Per-creator analytics payload. Items ordered `completed_at`
 * DESC server-side (TRD KD-5); FE never re-sorts.
 *
 * `total_published` is the brand's *currently-published* masterclass
 * count (M in "N of M"; TRD KD-7). When `total_published === 0` the FE
 * count chip drops the denominator + empty copy switches to "No training
 * published yet."
 */
export interface CompletedMasterclassesPayload {
  count: number
  total_published: number
  items: CompletedMasterclassItem[]
}

// ─── OKS-1038 — the member's collabs on the crew member page ─────────────

/**
 * One dated step of a collab (`Collabs::Timeline`). All five keys are always
 * present. `kind: "state"` steps use `Collab::STATES` names; `kind: "fact"`
 * steps are overlays that never move `last_state_change_at`.
 */
export interface CollabTimelineStep {
  kind: "state" | "fact"
  key:
    | BrandMissionCollabRow["state"]
    | "address_confirmed"
    | "product_chosen"
    | "no_ship"
    | "no_ship_voided"
    | "received"
  at: string
  /** `issue_note` on the `issue` step, else null. */
  note: string | null
  /**
   * `address_confirmation_source` on `address_confirmed`,
   * `product_choice_source` on `product_chosen` (OKS-1042), else null.
   */
  source: AddressConfirmationSource | ProductChoiceSource | null
}

export type AddressConfirmationSource = "creator" | "profile_auto" | "brand"

/**
 * OKS-1042 — the provenances the « Produit choisi » fact may carry.
 *
 * ⚠️ `"legacy"` is deliberately ABSENT. The column admits it (OKS-1040
 * backfill) and the DB CHECK permits `('legacy', <a timestamp>)`, but
 * `Collabs::Timeline::PRODUCT_CHOICE_FACT_SOURCES` never emits the fact for
 * it. Leaving it out means a server change that started emitting it is a
 * COMPILE error here, not a silent fallthrough to the raw key.
 */
export type ProductChoiceSource = "creator" | "brand"

/** PRD R6 — resolved server-side; the FE never re-derives the ladder. */
export interface CrewMemberCollabGifting {
  status:
    | "no_ship"
    | "address_pending"
    | "address_confirmed"
    | "shipped"
    | "received"
    | "delivered"
  confirmation_source: AddressConfirmationSource | null
  tracking_number: string | null
  tracking_url: string | null
}

/**
 * The brand collab row as the member page receives it
 * (`Collabs::BrandRowSerializer`, `surface: :crew_member`).
 *
 * ⚠️ The panel-only payloads are OMITTED, not optional: the server never
 * merges the address, sizes or product blocks on this surface (PRD R2), and
 * the type says so. It still satisfies every brand collab dialog, which read
 * only `id`, `creator` and the `can_*` flags.
 */
export type CrewMemberCollabRow = Omit<
  BrandMissionCollabRow,
  | "product"
  | "creator_sizes"
  | "shipping_address"
  | "shipped_variant_label"
  | "shipped_product_variant_id"
> & {
  mission: {
    id: number
    title: string
    mission_type: "open" | "drop" | "promotional"
    status: string
    drops_at: string | null
    gift_product_name: string | null
  }
  gifting: CrewMemberCollabGifting | null
  content: {
    latest_state: "pending" | "approved" | "flagged" | null
    total: number
    cap: number
  }
  timeline: CollabTimelineStep[]
}

export interface CrewMemberCollabHistory {
  items: CrewMemberCollabRow[]
  total_count: number
  /** The window size the server applied (`?collabs=`, 20..200). */
  limit: number
  empty_copy_key: string
}
